import * as React from 'react';
import {
  Grid,
  Form,
  FormBody,
  FormFooter,
  FormGroup,
  Label,
  LabelText,
  InputMessage,
  InputAddonNumberControls,
  useUpDownNumberControl,
  InputAddonText,
  Input,
  Button,
  DropDown,
  InfoBox,
} from '@zen-common/components-base';
import { FpUtils, TickerAutoSuggest } from '@zen/common-app-parts';
import { VerifyEventStore } from '../../stores/FixedPayoutStore';
import _oracles from '../../utils/oracles';
import OraclesDropDownFG from '../form/edit/OraclesDropDownFG';
import useFPFormDate from './useFPFormDate';

const oracles = _oracles.map((o) => ({ label: `${o.chain} ${o.address}`, value: o.address }));

/**
 * @param {Object} props
 * @param {Function} props.onSubmit
 */
export default function VerifyEventForm({ onSubmit, naming }) {
  const {
    actions,
    state: { oracle, price, ticker, collateralAsset, valid },
  } = VerifyEventStore.useStore();
  const formValid = valid;
  const { dayMonthYear, days, months, years } = useFPFormDate({
    setDate: actions.setDate,
  });

  const setOracle = (item) => {
    actions.setOracle(_oracles.find((o) => o.address === item.value) || null);
  };
  const setTicker = (item) => {
    actions.setTicker(item);
  };
  const reset = () => {
    actions.reset();
    dayMonthYear.reset();
  };

  const priceKeyHandler = useUpDownNumberControl({
    min: 0,
    value: price.value.safeValue,
    onChange: actions.setPrice,
  });

  const title = (asset, title) => FpUtils.getTitle({ asset, title, naming });

  const resetDisabled = React.useMemo(
    () =>
      collateralAsset.value === 'ZP' &&
      ticker.value === '' &&
      price.value.value === '' &&
      dayMonthYear.year === null &&
      dayMonthYear.month === null &&
      dayMonthYear.day === null,
    [
      collateralAsset.value,
      dayMonthYear.day,
      dayMonthYear.month,
      dayMonthYear.year,
      price.value.value,
      ticker.value,
    ]
  );

  return (
    <Form onSubmit={onSubmit}>
      <FormBody>
        <OraclesDropDownFG items={oracles} value={oracle} onChange={setOracle} />

        <Grid.Row>
          <Grid.Col100>
            <FormGroup>
              <InfoBox info="Held by the FPC">
                <Label htmlFor="verify-form-collateral-asset">
                  <LabelText>{title(collateralAsset.value, 'Collateral')}</LabelText>
                </Label>
              </InfoBox>
              <Input
                id="verify-form-collateral-asset"
                inputType="input"
                variant="block"
                value={collateralAsset.value}
                onChange={(e) => actions.setCollateralAsset(e.target.value.trim())}
                valid={collateralAsset.value ? collateralAsset.valid : undefined}
              />
              <InputMessage>
                {collateralAsset.value && !collateralAsset.valid && 'Invalid asset'}
              </InputMessage>
            </FormGroup>
          </Grid.Col100>
        </Grid.Row>

        <Grid.Row>
          <Grid.Col50>
            <FormGroup>
              <InfoBox info="Available tickers from data source">
                <Label htmlFor="ticker">
                  <LabelText>Ticker</LabelText>
                </Label>
              </InfoBox>
              <TickerAutoSuggest
                ticker={ticker.value}
                onTickerChanged={setTicker}
                tickerValid={ticker.valid}
              />
              <InputMessage></InputMessage>
            </FormGroup>
          </Grid.Col50>
          <Grid.Col50>
            <FormGroup>
              <Label htmlFor="strike-price">
                <LabelText>Strike Price (potential)</LabelText>
              </Label>
              <Input
                id="strike-price"
                variant="block"
                value={price.value.display}
                valid={price.value.display ? price.valid : undefined}
                autoComplete="off"
                type="text"
                onChange={(e) => actions.setPrice(e.target.value.trim())}
                renderLeftAddon={<InputAddonText>$</InputAddonText>}
                renderRightAddon={
                  <>
                    <InputAddonNumberControls
                      onChange={actions.setPrice}
                      value={price.value.safeValue}
                      min="0"
                    />
                  </>
                }
                {...priceKeyHandler}
              />
            </FormGroup>
          </Grid.Col50>
        </Grid.Row>

        <FormGroup>
          <Grid.Row>
            <Grid.Col33>
              <Label htmlFor="expiry-date-year">
                <LabelText>Expiry Year</LabelText>
              </Label>
              <DropDown
                id="expiry-date-year"
                placeholder="Year"
                items={years}
                value={dayMonthYear.year}
                onChange={(item) => dayMonthYear.setYear(item.value)}
              />
            </Grid.Col33>
            <Grid.Col33>
              <Label htmlFor="expiry-date-month">
                <LabelText>Expiry Month</LabelText>
              </Label>
              <DropDown
                id="expiry-date-month"
                placeholder="Month"
                items={months}
                value={dayMonthYear.month}
                onChange={(item) => dayMonthYear.setMonth(item.value)}
              />
            </Grid.Col33>
            <Grid.Col33>
              <Label htmlFor="expiry-date-day">
                <LabelText>Expiry Day</LabelText>
              </Label>
              <DropDown
                id="expiry-date-day"
                placeholder="Day"
                items={days}
                value={dayMonthYear.day}
                onChange={(item) => dayMonthYear.setDay(item.value)}
              />
            </Grid.Col33>
          </Grid.Row>
          <InputMessage></InputMessage>
        </FormGroup>

        <FormFooter>
          <Button bg="secondary" onClick={reset} disabled={resetDisabled}>
            Reset
          </Button>
          <Button type="submit" disabled={!formValid}>
            Verify
          </Button>
        </FormFooter>
      </FormBody>
    </Form>
  );
}
